'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["objectTypeLabel"];
import { createSelector } from 'reselect';
import { Set as ImmutableSet } from 'immutable';
import { getAllUsedFormFields } from 'FormsUI/selectors/FormDefinition';
import { isContactProperty } from 'FormsCommonComponents/utils/PropertyObjectTypes';
import { EmailField } from 'ui-forms-components/constants/FieldTypeMapping';
import { UnknownProperties } from 'forms-utils-lib/constants/Properties';
import { getObjectTypeIdValue } from 'FormsCommonComponents/js/form-definition/utils/FormFieldUtils';
import { getFormLanguage } from 'FormsUI/selectors/FormDefinition';
import { getTranslatedProperty } from 'FormsUI/utils/FormTranslationUtils';
import { getFormFieldsByPropertyId } from 'FormsUI/selectors/FormDefinition';
import { getLabelForObject } from 'FormsUI/utils/CustomObjects';
import * as RequestState from 'FormsCommonComponents/lib/RequestState';
import { getSubmissionBootstrapRequestState, getObjectTypeLabels } from 'FormsUI/selectors/SubmissionsBootstrap';
import getUnknownObjects from 'FormsUI/constants/UnknownObjects';
const root = state => state.customObjects || {};
export const customObjectsSelector = state => root(state).objectTypes;
export const flattenedObjectsSelector = state => root(state).flattenedObjects;
export const requiredPropertiesSelector = state => root(state).requiredProperties;
const getPropertiesCountForDisplay = co => Object.assign({}, co, {
  propertiesCount: co.propertyGroups && co.propertyGroups.reduce((acc, group) => acc += group.properties.length, 0) || 0
});
export const searchQuerySelector = state => state.properties.searchQuery;
export const getCustomObjects = createSelector(customObjectsSelector, customObjects => customObjects.map(getPropertiesCountForDisplay));
export const getCustomObjectIds = createSelector(customObjectsSelector, customObjects => customObjects.map(co => co.objectTypeId));
export const getFlattenedCustomObjects = createSelector([flattenedObjectsSelector], flattenedObjects => flattenedObjects);
export const getFrequentlyUsedProperties = state => state.customObjects.frequentlyUsedProperties;
export const getCustomObjectsLoading = state => state.customObjects.isLoading;
export const getObjectById = (state, id) => customObjectsSelector(state).find(val => val.objectTypeId === id) || '';
const getCustomProperty = (state, propertyId) => {
  if (!propertyId) return {};
  const formLanguage = getFormLanguage(state);
  const flattenedObjects = getFlattenedCustomObjects(state);
  const [objectTypeId, propertyName] = propertyId.split('/');
  const formFields = getFormFieldsByPropertyId(state);
  let translatedProperty;
  const unknownObjects = getUnknownObjects(formLanguage);
  const allObjects = Object.assign({}, unknownObjects, flattenedObjects);
  if (flattenedObjects && allObjects[objectTypeId][propertyName]) {
    translatedProperty = getTranslatedProperty(formFields, allObjects[objectTypeId][propertyName], propertyId, formLanguage);
  }
  return translatedProperty;
};
const getAllWarnings = state => root(state).requiredProperties || {};
const getUniquePropertyTypes = formFields => [...new ImmutableSet(formFields.map(formField => formField.objectTypeId))];
export const isPropertyRequiredForObject = (state, {
  name: propertyName,
  objectTypeId,
  propertyObjectType
}) => {
  const id = getObjectTypeIdValue(objectTypeId, propertyObjectType);
  const isContactEmailField = isContactProperty(id) && propertyName === EmailField;
  const isPaymentField = propertyName === UnknownProperties.PAYMENT_LINK;
  const isOtherObjectRequiredField = Boolean((getAllWarnings(state)[id] || {})[propertyName]);
  return isContactEmailField || isPaymentField || isOtherObjectRequiredField;
};
export const getMissingPropertiesOfObjectTypes = createSelector(getAllUsedFormFields, getAllWarnings, (formFields = [], allWarnings = {}) => {
  const actualWarnings = [];
  getUniquePropertyTypes(formFields).forEach(objectTypeId => {
    const _ref = allWarnings[objectTypeId] || {},
      {
        objectTypeLabel = null
      } = _ref,
      properties = _objectWithoutPropertiesLoose(_ref, _excluded);
    if (objectTypeLabel !== null) {
      actualWarnings.push({
        objectTypeLabel,
        properties: Object.values(properties).filter(({
          name
        }) => !formFields.some(field => {
          return field.name === name;
        }))
      });
    }
  });
  return actualWarnings.filter(warning => warning.properties.length > 0);
});
export const getPropertyOrCustomProperty = (state, propertyId) => getCustomProperty(state, propertyId);
export const getSearchQueryResults = state => {
  return state.customObjects.objectTypeSearchResults.map(getPropertiesCountForDisplay);
};
export const getShowSearchResults = state => state.customObjects.showSearchResults;
export const getBootstrapRequestState = state => state.customObjects.BOOTSTRAP_REQUEST_STATE || RequestState.NOT_REQUESTED;
export const getIsPropertyAnnotationHintUITagShown = (state, objectTypeId) => {
  return getSubmissionBootstrapRequestState(state, window.location.href.match('forms/[0-9]+/([a-z0-9-]+)/')[1]).isSuccessful() && objectTypeId && getLabelForObject(objectTypeId, getObjectTypeLabels(state));
};